/**
 * Order tax — the ONE calculation every server and browser path uses.
 *
 * 🔴 WHY THIS EXISTS. Tax was computed in four places that disagreed: the till
 * (`TaxCalculationCubit`), online order creation (`orderService.ts`
 * `convertCartToOrderData`), the checkout preview (`apps/api` `orders.ts`) and
 * the web cart (`packages/client` `cart.ts`). The till takes an order-level
 * discount off the taxable amount; the three web copies taxed the FULL price
 * and subtracted the discount afterwards. The same basket with a $5 loyalty
 * redemption was charged more tax online than at the counter.
 *
 * THE TILL IS THE REFERENCE. This is a line-for-line port of `computeOrderTaxes`
 * + `routeOrderTaxes` in
 * `apps/pos/lib/blocs/pricing/tax_calculation/tax_calculation_cubit.dart`
 * (docs/TAX_EXEMPTION_ARCHITECTURE.md: "the POS tax engine is the only tax
 * calculation" for the till). The till cannot call this code — it runs on the
 * device, offline — so the two are held together by a fixture that BOTH
 * engines execute: `__tests__/fixtures/tax-parity.json`, vendored byte-for-byte
 * into the POS repo at `test/fixtures/tax-parity.json`
 * (`scripts/ops/check-tax-parity.mjs`). Change a rule here and the Dart side
 * goes red, and vice versa.
 *
 * The rules, in the till's words:
 *  - Only ACTIVE (non-voided) lines count.
 *  - A line's own discounts come off that line.
 *  - Order-level discounts are spread over EVERY active line in proportion to
 *    its post-line-discount price — taxable or not — as a factor in [0, 1].
 *  - A line with a positive `customTaxRate` is taxed at that rate alone (as
 *    `Custom Tax`, id -1), first; a line carrying ANY `customTaxRate` (0
 *    included) is skipped by the shared rates.
 *  - Shared rates are walked in the order given; a rate whose base is not
 *    positive is not applied at all.
 *  - Percent = base × (rate / 100); Fixed = rate × units; Tax Table / None = 0.
 *  - NO rounding: amounts are raw doubles, exactly as the till produces them.
 *    Round once, at the money boundary, in the caller (`roundToCents`).
 *  - Exempt: nothing applied; the same taxes are reported as waived.
 *
 * Operand ORDER matters for bit-identical doubles (`base * (rate / 100)`, not
 * `(base * rate) / 100`) — keep it exactly as the Dart source writes it.
 */

/** Rate kinds as Firestore spells them. */
export type TaxEngineRateType = 'Percent' | 'Fixed' | 'Tax Table' | 'None';

export interface TaxEngineRate {
  readonly taxRateId?: number;
  readonly name: string;
  readonly rate: number;
  /** Any spelling; normalised the way the till's `TaxRate.fromMap` does. */
  readonly type?: string;
  /**
   * A disabled rate is not a tax. Callers normally pass only active rates; this
   * is honoured here too so a stray disabled rate can never charge a guest.
   * The till's loaded rate list DOES include disabled rates, so its engine
   * skips them the same way (A7) — pinned by the corpus case "a disabled rate
   * charges nothing".
   */
  readonly disabled?: boolean;
}

export interface TaxEngineLine {
  /** Price with modifiers × quantity, BEFORE any discount. */
  readonly lineSubtotal: number;
  /** Σ this line's own discounts. */
  readonly lineDiscount?: number;
  readonly quantity: number;
  readonly isTaxable: boolean;
  readonly taxRateIds: readonly number[];
  readonly customTaxRate?: number | null;
  readonly voided?: boolean;
}

export interface TaxEngineInput {
  readonly lines: readonly TaxEngineLine[];
  readonly taxRates: readonly TaxEngineRate[];
  /** Σ order-level discounts (loyalty redemption, coupon, comp). */
  readonly orderDiscountTotal?: number;
  readonly taxExempt?: boolean;
}

export interface ComputedTax {
  readonly taxRateId: number;
  readonly name: string;
  readonly rate: number;
  readonly type: TaxEngineRateType;
  readonly taxableBaseAmount: number;
  readonly amountCalculated: number;
}

export interface OrderTaxResult {
  /** What the guest is charged, one entry per rate. Empty when exempt. */
  readonly appliedTaxes: readonly ComputedTax[];
  /** Σ appliedTaxes — raw, unrounded. 0 when exempt. */
  readonly taxAmount: number;
  /** Union taxable base: each taxed line counted once however many rates hit it. */
  readonly taxableAmount: number;
  /** Exempt only: the taxes that were NOT charged. */
  readonly waivedTax: {
    readonly taxableAmount: number;
    readonly taxAmount: number;
    readonly taxes: readonly ComputedTax[];
  } | null;
}

/** The till's `_taxTypeFromString`: lower-cased, unknown reads as Percent. */
export function normalizeTaxRateType(value: unknown): TaxEngineRateType {
  switch (String(value ?? '').toLowerCase()) {
    case 'fixed':
      return 'Fixed';
    case 'taxtable':
    case 'tax table':
      return 'Tax Table';
    case 'none':
      return 'None';
    case 'percent':
    default:
      return 'Percent';
  }
}

/** The till's `resolveTaxAmount`. */
export function resolveTaxAmount(
  type: TaxEngineRateType,
  rate: number,
  taxableBaseAmount: number,
  taxableQuantity: number,
): number {
  switch (type) {
    case 'Percent':
      return taxableBaseAmount * (rate / 100);
    case 'Fixed':
      return rate * taxableQuantity;
    default:
      return 0;
  }
}

const priceAfterItemDiscounts = (line: TaxEngineLine): number =>
  line.lineSubtotal - (line.lineDiscount ?? 0);

/** The till's `orderDiscountTaxFactor`. */
export function orderDiscountTaxFactor(
  activeLines: readonly TaxEngineLine[],
  orderDiscountTotal: number,
): number {
  if (!(orderDiscountTotal > 0)) return 1.0;
  const base = activeLines.reduce((sum, line) => sum + priceAfterItemDiscounts(line), 0.0);
  if (base <= 0) return 1.0;
  const factor = 1.0 - orderDiscountTotal / base;
  return factor < 0 ? 0.0 : factor;
}

export function computeOrderTaxes(input: TaxEngineInput): OrderTaxResult {
  const lines = input.lines.filter((line) => !line.voided);
  const discountFactor = orderDiscountTaxFactor(lines, input.orderDiscountTotal ?? 0);
  const taxes: ComputedTax[] = [];
  // Insertion-ordered, like Dart's default LinkedHashSet.
  const taxedLineIndexes = new Set<number>();

  lines.forEach((line, i) => {
    const custom = line.customTaxRate;
    if (line.isTaxable && custom != null && custom > 0) {
      const taxableAmount = priceAfterItemDiscounts(line) * discountFactor;
      taxedLineIndexes.add(i);
      taxes.push({
        taxRateId: -1,
        name: 'Custom Tax',
        rate: custom,
        type: 'Percent',
        taxableBaseAmount: taxableAmount,
        amountCalculated: taxableAmount * (custom / 100),
      });
    }
  });

  for (const taxRate of input.taxRates) {
    if (taxRate.disabled === true || taxRate.taxRateId == null) continue;
    let taxableBaseAmount = 0.0;
    let taxableQuantity = 0;
    const contributing: number[] = [];

    lines.forEach((line, i) => {
      if (
        line.isTaxable &&
        line.customTaxRate == null &&
        line.taxRateIds.includes(taxRate.taxRateId as number)
      ) {
        taxableBaseAmount += priceAfterItemDiscounts(line) * discountFactor;
        taxableQuantity += line.quantity;
        contributing.push(i);
      }
    });

    if (taxableBaseAmount > 0) {
      contributing.forEach((i) => taxedLineIndexes.add(i));
      const type = normalizeTaxRateType(taxRate.type);
      taxes.push({
        taxRateId: taxRate.taxRateId,
        name: taxRate.name,
        rate: taxRate.rate,
        type,
        taxableBaseAmount,
        amountCalculated: resolveTaxAmount(type, taxRate.rate, taxableBaseAmount, taxableQuantity),
      });
    }
  }

  let taxableAmount = 0.0;
  for (const i of taxedLineIndexes) {
    taxableAmount += priceAfterItemDiscounts(lines[i]!) * discountFactor;
  }
  const computedTax = taxes.reduce((sum, tax) => sum + tax.amountCalculated, 0.0);

  if (input.taxExempt === true) {
    return {
      appliedTaxes: [],
      taxAmount: 0,
      taxableAmount,
      waivedTax: { taxableAmount, taxAmount: computedTax, taxes },
    };
  }
  return { appliedTaxes: taxes, taxAmount: computedTax, taxableAmount, waivedTax: null };
}

/**
 * A web cart line as the tax engine sees it. Structural so `@order-up/common`
 * need not depend on the `CartItem` class. The base is `totalPrice` — the same
 * figure the web cart, the preview and order creation have always taxed — and
 * a cart line is taxable exactly when it names a rate (what order creation
 * stamps as `isTaxable`). Cart lines carry no line discounts.
 */
export interface CartLikeTaxLine {
  readonly totalPrice: number;
  readonly quantity: number;
  readonly taxRateIds?: readonly number[] | null;
}

export function taxLinesFromCartItems(items: readonly CartLikeTaxLine[]): TaxEngineLine[] {
  return items.map((item) => {
    const taxRateIds = item.taxRateIds ?? [];
    return {
      lineSubtotal: item.totalPrice,
      quantity: item.quantity,
      isTaxable: taxRateIds.length > 0,
      taxRateIds,
    };
  });
}
